import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Car, 
  ArrowLeft, 
  FileText, 
  CheckCircle, 
  AlertTriangle,
  ExternalLink,
  Download,
  HelpCircle,
  Clock,
  MapPin
} from "lucide-react";

const QuitusFiscalGuide = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#000091] rounded-lg flex items-center justify-center">
              <Car className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-[#000091]">Carte Grise Étrangère</h1>
              <p className="text-xs text-slate-500">Guide - Quitus Fiscal</p>
            </div>
          </div>
        </div>
        <div className="h-1 flex">
          <div className="flex-1 bg-[#002395]"></div>
          <div className="flex-1 bg-white"></div>
          <div className="flex-1 bg-[#ED2939]"></div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="mb-6 text-slate-600"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>

        <div className="space-y-8">
          {/* Introduction */}
          <div>
            <h1 className="text-3xl font-bold text-[#0F172A] mb-4">
              Comment obtenir votre Quitus Fiscal ?
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Le quitus fiscal est un document obligatoire pour immatriculer un véhicule acheté dans un autre pays de l'Union Européenne. 
              Il atteste que la TVA a bien été acquittée ou qu'elle n'est pas due.
            </p>
          </div>

          {/* Alert */}
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4 flex items-start gap-3">
              <HelpCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-blue-800 font-medium">Bon à savoir</p>
                <p className="text-sm text-blue-700 mt-1">
                  Le quitus fiscal est gratuit et s'obtient généralement sous 24 à 48h auprès de votre centre des impôts. 
                  Vous pouvez également faire la demande en ligne sur impots.gouv.fr.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Step by step */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#0F172A]">
                <FileText className="w-5 h-5 text-[#000091]" />
                Étapes pour obtenir le quitus fiscal
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Step 1 */}
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-[#000091] text-white flex items-center justify-center font-bold flex-shrink-0">
                  1
                </div>
                <div>
                  <h3 className="font-semibold text-[#0F172A] mb-2">Télécharger le formulaire CERFA 1993-PART</h3>
                  <p className="text-slate-600 text-sm mb-3">
                    Ce formulaire est la "Demande de certificat fiscal pour un véhicule acquis dans l'Union Européenne".
                  </p>
                  <a 
                    href="https://www.impots.gouv.fr/formulaire/1993-part-sd/certificat-fiscal-exigible-lors-de-la-premiere-immatriculation-en-france-dun" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#000091] hover:underline text-sm font-medium"
                  >
                    <Download className="w-4 h-4" />
                    Télécharger le CERFA 1993-PART
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-[#000091] text-white flex items-center justify-center font-bold flex-shrink-0">
                  2
                </div>
                <div>
                  <h3 className="font-semibold text-[#0F172A] mb-2">Remplir le formulaire</h3>
                  <p className="text-slate-600 text-sm mb-3">
                    Complétez les informations suivantes :
                  </p>
                  <ul className="space-y-2 text-sm text-slate-600">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span><strong>Cadre A :</strong> Vos coordonnées (nom, adresse, téléphone)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span><strong>Cadre B :</strong> Informations du véhicule (marque, type, n° série VIN, date 1ère mise en circulation)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span><strong>Cadre C :</strong> Prix d'achat TTC et pays d'acquisition</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span><strong>Cadre D :</strong> Cochez la case correspondant à votre situation (véhicule d'occasion de plus de 6 mois = pas de TVA à payer)</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-[#000091] text-white flex items-center justify-center font-bold flex-shrink-0">
                  3
                </div>
                <div>
                  <h3 className="font-semibold text-[#0F172A] mb-2">Rassembler les pièces justificatives</h3>
                  <ul className="space-y-2 text-sm text-slate-600">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>Copie de la carte grise étrangère (recto/verso)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>Facture d'achat ou certificat de cession</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>Copie de votre pièce d'identité</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Step 4 */}
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-[#000091] text-white flex items-center justify-center font-bold flex-shrink-0">
                  4
                </div>
                <div>
                  <h3 className="font-semibold text-[#0F172A] mb-2">Déposer votre demande</h3>
                  <p className="text-slate-600 text-sm mb-3">
                    Vous avez plusieurs options :
                  </p>
                  <div className="space-y-3">
                    <div className="bg-slate-50 rounded-lg p-4">
                      <p className="font-medium text-[#0F172A] mb-1">Option 1 : En ligne (recommandé)</p>
                      <p className="text-sm text-slate-600 mb-2">
                        Via votre espace particulier sur impots.gouv.fr, rubrique "Messagerie sécurisée" puis "Écrire" et sélectionnez "J'ai une autre demande".
                      </p>
                      <a 
                        href="https://www.impots.gouv.fr" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-[#000091] hover:underline text-sm font-medium"
                      >
                        Accéder à impots.gouv.fr
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4">
                      <p className="font-medium text-[#0F172A] mb-1">Option 2 : En personne</p>
                      <p className="text-sm text-slate-600">
                        Rendez-vous au Service des Impôts des Particuliers (SIP) de votre domicile avec le formulaire et les pièces justificatives.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 5 */}
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center font-bold flex-shrink-0">
                  5
                </div>
                <div>
                  <h3 className="font-semibold text-[#0F172A] mb-2">Réception du quitus fiscal</h3>
                  <p className="text-slate-600 text-sm">
                    Le quitus fiscal vous sera délivré généralement sous 24 à 48h. Il porte le cachet de l'administration fiscale et mentionne 
                    soit "TVA acquittée", soit "Dispense de TVA" (pour les véhicules d'occasion de plus de 6 mois).
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Important info */}
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-amber-800 font-medium">Cas particuliers - TVA à payer</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Si votre véhicule est considéré comme "neuf" au sens fiscal (moins de 6 mois OU moins de 6000 km), 
                    vous devrez payer la TVA française de 20% sur le prix d'achat. Le quitus fiscal vous indiquera le montant à régler.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Delay info */}
          <Card className="border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#000091]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-[#000091]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#0F172A] mb-2">Délais de traitement</h3>
                  <p className="text-slate-600 text-sm">
                    En ligne : 24 à 48h<br/>
                    En personne : Immédiat ou sous 24h selon le centre des impôts
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* CTA */}
          <div className="text-center pt-4">
            <p className="text-slate-600 mb-4">Une fois votre quitus fiscal obtenu, vous pouvez continuer votre demande d'immatriculation.</p>
            <Button 
              onClick={() => navigate('/demande')}
              className="bg-[#000091] hover:bg-[#000091]/90"
            >
              Continuer ma demande
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default QuitusFiscalGuide;
